import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Fingerprint, CheckCircle2, Loader2, AlertCircle, X } from 'lucide-react';
import { API } from '../auth/AuthContext';
import {
  isPlatformAuthenticatorAvailable,
  registerBiometric,
  rememberBiometricEmail,
} from '../auth/webauthn';

export default function BiometricModal({ open, onClose, user, onDone }) {
  const [supported, setSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!open) { setError(''); setOk(false); setBusy(false); return; }
    (async () => setSupported(await isPlatformAuthenticatorAvailable()))();
  }, [open]);

  if (!open) return null;

  const enroll = async () => {
    setError(''); setBusy(true);
    try {
      const { data: ch } = await axios.get(`${API}/auth/biometric/challenge`, { withCredentials: true });
      const reg = await registerBiometric({
        challenge: ch.challenge,
        userId: user.user_id,
        userName: user.email,
        userDisplayName: user.name,
      });
      await axios.post(
        `${API}/auth/biometric/register`,
        { credential_id: reg.credential_id, public_key: reg.public_key, device_label: navigator.userAgent.slice(0, 80) },
        { withCredentials: true }
      );
      rememberBiometricEmail(user.email, reg.credential_id);
      setOk(true);
      setTimeout(() => { onDone?.(); onClose?.(); }, 1400);
    } catch (err) {
      if (err?.name === 'NotAllowedError') setError('Cadastro cancelado.');
      else setError(err?.response?.data?.detail || err?.message || 'Falha no cadastro biométrico.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900">Ativar biometria</h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-5">
          {ok ? (
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-900">Biometria ativa!</p>
              <p className="text-xs text-slate-500 mt-1">Da próxima vez, use "Entrar com biometria" na tela de login.</p>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center mx-auto mb-3">
                <Fingerprint className="w-7 h-7" />
              </div>
              <p className="text-sm text-slate-700 text-center">
                Use Touch ID, Face ID ou a biometria do seu dispositivo para acessar o BioPraga OS sem digitar a senha.
              </p>
              <p className="text-[11px] text-slate-500 mt-2 text-center">
                A credencial fica atrelada apenas a este aparelho.
              </p>
              {!supported && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800">Este aparelho/navegador não possui um autenticador biométrico disponível.</p>
                </div>
              )}
              {error && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-rose-700">{error}</p>
                </div>
              )}
              <button
                onClick={enroll}
                disabled={!supported || busy}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white text-sm font-semibold rounded-lg"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                Ativar neste dispositivo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
